<template>
    <div
        v-click-outside="hide"
        class="tooltip-wrapper inline-block font-sans text-sm font-normal normal-case leading-normal absolute"
        style="letter-spacing: 0"
        @click="show"
        @mouseleave="show"
        @mouseover="show"
    >
        <div
            v-if="isVisible"
            ref="arrowElement"
            :style="tooltipArrowStyles"
            class="tooltip-arrow"
        />

        <span
            ref="triggerElement"
            class="cursor-help"
        >
            <slot name="default" />
        </span>

        <div
            v-if="isVisible"
            ref="tooltipElement"
            :style="floatingStyles"
            class="tooltip-content absolute top-0 left-0 z-50 bg-white text-black drop-shadow-lg"
            @click="hide"
        >
            <div class="p-6 flex flex-row gap-4">
                <div
                    v-if="slots.icon"
                    class="w-5"
                >
                    <slot name="icon" />
                </div>

                <div class="max-w-full w-[80%] lg:w-64">
                    <slot name="tooltip" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { Placement } from "@floating-ui/vue";
import { arrow, autoPlacement, autoUpdate, shift, useFloating } from "@floating-ui/vue";

const slots = useSlots();

const props = withDefaults(
    defineProps<{
        position?: Placement;
        positionAlternate?: Placement;
    }>(),
    {
        position: "right",
        positionAlternate: "bottom",
    },
);

const isVisible = ref(false);
const arrowElement = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
const tooltipElement = ref<HTMLElement | null>(null);
const { floatingStyles, middlewareData, placement } = useFloating(triggerElement, tooltipElement, {
    placement: props.position,
    transform: false,
    whileElementsMounted(...args) {
        return autoUpdate(...args, { animationFrame: true });
    },
    middleware: [
        autoPlacement({
            allowedPlacements: [props.position, props.positionAlternate],
        }),
        shift(),
        arrow({ element: arrowElement }),
    ],
});

const show = () => {
    isVisible.value = true;
};

const hide = () => {
    isVisible.value = false;
};

const tooltipArrowStyles = computed(() => {
    const arrowColor = "white";
    let borderColor = null;

    if (placement.value === "right") {
        borderColor = `transparent ${arrowColor} transparent transparent`;
    } else if (placement.value === "left") {
        borderColor = `transparent transparent transparent ${arrowColor}`;
    } else if (placement.value === "top") {
        borderColor = `${arrowColor} transparent transparent transparent`;
    } else if (placement.value === "bottom") {
        borderColor = `transparent transparent ${arrowColor} transparent`;
    }

    return {
        left: middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : "",
        top: middlewareData.value.arrow?.y != null ? `${middlewareData.value.arrow.y}px` : "",
        "--tooltipArrowBorderColor": borderColor,
    };
});

onMounted(() => {
    window.addEventListener("scroll", hide);
    window.addEventListener("resize", hide);
});

onUnmounted(() => {
    window.removeEventListener("scroll", hide);
    window.removeEventListener("resize", hide);
});
</script>

<style scoped>
@reference "../../assets/css/app.css";

.tooltip-wrapper {
    @apply bg-clip-border bg-none;

    -webkit-text-fill-color: currentColor;
}

.tooltip-arrow {
    @apply absolute w-4 h-4 z-10;
}

.tooltip-arrow::before {
    @apply absolute border-8 border-solid;
    content: "";
    border-color: var(--tooltipArrowBorderColor);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    z-index: -1;
}

.tooltip-arrow::after {
    @apply absolute border-8 border-solid;
    content: "";
    border-color: var(--tooltipArrowBorderColor);
}
</style>
